import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getFeaturedArticles, noteCategories } from '@/mocks/notes';

export default function NotesPreviewSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const featured = getFeaturedArticles().slice(0, 4);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-20 md:py-28 bg-[#faf8f5]">
      <div className="w-full px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div className={`flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-6 h-px bg-[#b08060]"></div>
              <span className="text-[0.8125rem] tracking-[0.2em] text-[#b08060] font-medium uppercase">Mingzheng Notes</span>
            </div>
            <h2
              className="font-serif-tc text-[#2c2420] leading-[1.3] mb-4"
              style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}
            >
              鳴箏筆記
            </h2>
            <p className="text-[1.0625rem] leading-[1.75] text-[#7a6e62] max-w-md">
              關於古箏學習、選箏指南與教學日常的文字。<br />
              寫給每一個對音樂有好奇、也有猶豫的人。
            </p>
          </div>
          <Link
            to="/notes"
            className="inline-flex items-center gap-2 text-[0.9375rem] text-[#7a6248] font-medium hover:gap-4 transition-all duration-300 cursor-pointer whitespace-nowrap self-start md:self-auto"
          >
            閱讀更多文章 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Category Chips */}
        <div className={`flex flex-wrap gap-2.5 mb-10 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {noteCategories.map((cat) => (
            <Link
              key={cat.id}
              to={`/notes#${cat.id}`}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-[#d4c4b0] text-[0.875rem] text-[#7a6e62] hover:bg-[#7a6248] hover:text-white hover:border-[#7a6248] transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className={`${cat.icon} text-sm`}></i>
              {cat.label}
            </Link>
          ))}
        </div>

        {/* Article Cards Grid */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 transition-all duration-700 delay-150 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {featured.map((article, idx) => (
            <Link
              key={article.slug}
              to={`/notes/${article.slug}`}
              className="group block bg-white rounded-xl border border-[#ede5d8] hover:border-[#c8b89a] overflow-hidden transition-all duration-300 cursor-pointer"
              style={{ transitionDelay: `${idx * 60}ms` }}
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                  <span className="px-2.5 py-1 rounded-full bg-white/90 text-[#7a6248] text-[0.75rem] font-medium">
                    {article.categoryLabel}
                  </span>
                </div>
                {/* Teacher Pick Badge */}
                <div className="absolute top-3 right-3">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#5a3e22]/85 text-white text-[0.68rem] font-medium backdrop-blur-sm whitespace-nowrap">
                    <i className="ri-quill-pen-line text-[0.68rem]"></i>
                    老師推薦
                  </span>
                </div>
              </div>
              <div className="p-5">
                <h3
                  className="font-serif-tc text-[#2c2420] leading-[1.4] mb-3 group-hover:text-[#7a6248] transition-colors"
                  style={{ fontSize: '1rem' }}
                >
                  {article.title}
                </h3>
                <p className="text-[0.875rem] leading-[1.65] text-[#7a6e62] line-clamp-2">
                  {article.excerpt}
                </p>
                <div className="flex items-center gap-2 mt-4 pt-3.5 border-t border-[#f0e8dc] text-[0.8125rem] text-[#a89880]">
                  <span>約 {article.readTime} 分鐘</span>
                  <span>·</span>
                  <span>{article.publishedAt}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Bottom CTA strip */}
        <div className={`mt-12 p-6 md:p-8 rounded-2xl bg-[#f5f0e8] border border-[#e0d5c5] flex flex-col sm:flex-row items-center justify-between gap-5 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div>
            <p className="font-serif-tc text-[#2c2420] text-[1.125rem] mb-1">還有更多文章，等你來看</p>
            <p className="text-[0.9375rem] text-[#7a6e62]">初學入門・選箏指南・老師筆記・常見問題，每一篇都寫給真實的你。</p>
          </div>
          <Link
            to="/notes"
            className="px-6 py-3 rounded-full bg-[#7a6248] text-white text-[0.9375rem] font-medium hover:bg-[#6a5238] transition-all cursor-pointer whitespace-nowrap"
          >
            前往鳴箏筆記
          </Link>
        </div>
      </div>
    </section>
  );
}
